import {
  ProviderUnavailableError,
  type LLMProvider,
  type LLMRequest,
  type LLMResult,
  type ProviderCallOptions,
  type VisionProvider,
  type VisionRequest,
  type VisionVerdict,
} from "@loumy/contracts";
import { resolveVisionVerdict, visionGateSystem } from "./fal-vision";
import { safeFetchBytes } from "./safe-fetch";

// §7 `vision` + `llm`-Provider → Google Gemini DIREKT (D60). Nutzt die Google Developer API
// (generativelanguage.googleapis.com), NICHT Vertex/fal — ein einfacher API-Key reicht.
// ECHTE Adapter, ENV-gated (ohne Key → ProviderUnavailableError, §13).
//
// Dokumentierter Contract (ai.google.dev/api/generate-content, verifiziert 2026-06-01):
//   Endpoint : POST {base}/v1beta/models/{model}:generateContent
//   Auth     : Header `x-goog-api-key: <KEY>`   (Key NIE in URL/Logs/Fehlern leaken)
//   Body     : { contents:[{ role:"user", parts:[ {text} | {inline_data:{mime_type,data}} ] }],
//               systemInstruction?:{ parts:[{text}] }, generationConfig?:{ temperature,
//               maxOutputTokens, … } }
//   Antwort  : { candidates:[{ content:{ parts:[{text}] }, finishReason? }], promptFeedback?:{ blockReason } }
//   Bilder   : Gemini fetcht KEINE URLs — der Adapter lädt das Bild selbst (per `safeFetchBytes`:
//              https-only, kein Redirect, Größen-/MIME-Limit, private Hosts geblockt — SSRF-
//              Defense-in-Depth, da die imageUrl letztlich eine R2-presignte/Quell-URL ist, D56).
//   Safety   : prompt-Sperren (`promptFeedback.blockReason`) UND candidate-Sperren
//              (`finishReason ∈ SAFETY/RECITATION/…`) werden als `blockReason` vereinheitlicht:
//              VISION urteilt dann fail-closed `reject` (sauberes Gate + D52-Refund, kein Job-
//              Error), LLM `complete` WIRFT (fail-closed, kein stilles Degradieren auf Roh-Prompt).

const GEMINI_DEFAULT_BASE_URL = "https://generativelanguage.googleapis.com";
// Vom User gewähltes Default-Modell; über VISION_MODEL/LLM_MODEL überschreibbar, da die exakte
// Google-Modell-ID abweichen kann (z.B. dateierter Snapshot).
const GEMINI_DEFAULT_MODEL = "gemini-3.1-flash-lite";
// candidate.finishReason-Werte, die einen Safety-/Policy-Abbruch bedeuten (≠ STOP/MAX_TOKENS).
// `OTHER`/`LANGUAGE` bewusst NICHT hart blockend: bei leerem Text greift schon der korrekte
// Slot-Fallback (Vision→fail-closed reject via leerem Output, LLM→Passthrough); als Hard-Block
// wären sie für den LLM-Pfad zu aggressiv (würden valide Teiltexte verwerfen).
const BLOCKING_FINISH = new Set(["SAFETY", "RECITATION", "PROHIBITED_CONTENT", "BLOCKLIST", "SPII", "IMAGE_SAFETY"]);

/** Leere/blanke Env-Strings (`""`) zählen als „nicht gesetzt" ⇒ Default greift (P1.2). */
function orDefault(value: string | undefined, fallback: string): string {
  const v = value?.trim();
  return v && v.length > 0 ? v : fallback;
}

/** Gemeinsame Konfiguration beider Gemini-Adapter (Key/Endpoint/Modell). */
export interface GeminiConfig {
  apiKey?: string;
  baseUrl?: string;
  model?: string;
  /** Unparsebarer VLM-Output → `pass` statt `reject`? Default `false` (fail-closed, D52). Nur vision. */
  failOpen?: boolean;
  /** `edit`-Verdict zulassen? Nur sinnvoll mit echtem image-edit-Provider (§13). Default `false`. Nur vision. */
  allowEdit?: boolean;
}

interface GeminiPart {
  text?: string;
  inline_data?: { mime_type: string; data: string };
}

/**
 * Magic-Byte-Sniff → Bild-MIME (presignte R2-Objekte liefern oft `application/octet-stream`).
 * Unbekannte Signatur → `undefined` ⇒ der Aufrufer bricht **fail-closed** ab (kein HTML-/JSON-/
 * Fehlerbody als „image/png" an Gemini — SSRF-/Garbage-Defense).
 */
function sniffImageMime(b: Uint8Array): string | undefined {
  if (b.length >= 4 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return "image/png";
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "image/jpeg";
  if (b.length >= 4 && b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46) return "image/gif";
  if (b.length >= 12 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50)
    return "image/webp";
  return undefined;
}

/** Uint8Array → base64 (chunked btoa; Worker + Node global). */
function toBase64(bytes: Uint8Array): string {
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/**
 * Roher generateContent-Call → `{ text, blockReason }`. Wirft NUR bei HTTP-Fehlern (Transport,
 * fail-closed D34) — OHNE den Key zu leaken (nur Status in der Meldung). Safety-/Policy-Sperren
 * (prompt-Ebene `promptFeedback.blockReason` ODER candidate-Ebene `finishReason`) werden NICHT
 * geworfen, sondern als `blockReason` zurückgegeben; der Aufrufer entscheidet (Vision: reject,
 * LLM: throw). So bleibt das VLM-Gate sauber (reject statt Job-Error) und ein candidate-Safety-
 * Abbruch degradiert NICHT still zu leerem Text (P1.3).
 */
async function generateContent(opts: {
  apiKey: string;
  baseUrl: string;
  model: string;
  parts: GeminiPart[];
  system?: string;
  generationConfig?: Record<string, unknown>;
  signal?: AbortSignal;
}): Promise<{ text: string; blockReason: string | null }> {
  const body: Record<string, unknown> = { contents: [{ role: "user", parts: opts.parts }] };
  if (opts.system) body["systemInstruction"] = { parts: [{ text: opts.system }] };
  if (opts.generationConfig) body["generationConfig"] = opts.generationConfig;

  const res = await fetch(`${opts.baseUrl}/v1beta/models/${opts.model}:generateContent`, {
    method: "POST",
    headers: { "x-goog-api-key": opts.apiKey, "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal: opts.signal,
  });
  if (!res.ok) throw new Error(`Gemini: HTTP ${res.status} ${res.statusText}`); // Key NICHT leaken

  const json = (await res.json()) as {
    candidates?: { content?: { parts?: { text?: unknown }[] }; finishReason?: unknown }[];
    promptFeedback?: { blockReason?: unknown };
  };
  const candidate = json.candidates?.[0];
  const promptBlock = typeof json.promptFeedback?.blockReason === "string" ? json.promptFeedback.blockReason : null;
  const finish = typeof candidate?.finishReason === "string" ? candidate.finishReason : null;
  const finishBlock = finish && BLOCKING_FINISH.has(finish) ? finish : null;
  const parts = candidate?.content?.parts ?? [];
  const text = parts.map((p) => (typeof p.text === "string" ? p.text : "")).join("");
  return { text, blockReason: promptBlock ?? finishBlock };
}

/** §7 vision-Slot → Gemini multimodal (D50/D52/D60). Lädt das Bild als inline_data, urteilt. */
export class GeminiVisionProvider implements VisionProvider {
  readonly id = "gemini-vision";
  readonly version = 1;
  readonly displayName = "Google Gemini (Vision)";

  private readonly baseUrl: string;
  private readonly model: string;
  private readonly failOpen: boolean;
  private readonly allowEdit: boolean;

  constructor(private readonly config: GeminiConfig = {}) {
    this.baseUrl = orDefault(config.baseUrl, GEMINI_DEFAULT_BASE_URL).replace(/\/+$/, "");
    this.model = orDefault(config.model, GEMINI_DEFAULT_MODEL);
    this.failOpen = config.failOpen === true;
    this.allowEdit = config.allowEdit === true;
  }

  private requireKey(): string {
    if (!this.config.apiKey)
      throw new ProviderUnavailableError("vision", this.id, "VISION_API_KEY fehlt (Adapter nicht konfiguriert, §13).");
    return this.config.apiKey;
  }

  async judge(req: VisionRequest, opts?: ProviderCallOptions): Promise<VisionVerdict> {
    const apiKey = this.requireKey();
    if (!req.imageUrl)
      throw new ProviderUnavailableError("vision", this.id, "Vision-Gate braucht ein Eingabebild (imageUrl fehlt).");

    // Bild laden → base64 (Gemini fetcht keine URLs). SSRF-/DoS-gehärtet (P1.1): https-only,
    // kein Redirect, Größenlimit, private/interne Hosts geblockt. MIME: image/* aus dem
    // content-type, sonst Magic-Byte-Sniff (presignte R2-Objekte liefern oft octet-stream).
    // D76: opts.signal bricht Bild-Download + Gemini-Call bei Timeout/Cancel ab (Re-Validate-Schleife).
    const { bytes, contentType } = await safeFetchBytes(req.imageUrl, { maxBytes: 32 * 1024 * 1024, signal: opts?.signal });
    const mime = contentType.startsWith("image/") ? contentType : sniffImageMime(bytes);
    if (!mime) throw new Error("Gemini vision: Eingabe ist kein erkennbares Bild (MIME unbestimmbar) — fail-closed.");
    const data = toBase64(bytes);

    const userText = `User prompt: ${req.prompt}${req.action ? `\nAction: ${req.action}` : ""}${
      req.loop ? "\nLoop required: yes (judge loop feasibility)" : ""
    }\nJudge the attached image.`;
    const { text, blockReason } = await generateContent({
      apiKey,
      baseUrl: this.baseUrl,
      model: this.model,
      // D114: Re-Validate-Kontext + gepinnte Screen-Farbe der Edit-Schleife fließen ins Gate.
      system: visionGateSystem({
        loop: req.loop === true,
        revalidate: req.revalidate === true,
        ...(req.screenColor ? { screenColor: req.screenColor } : {}),
      }),
      parts: [{ text: userText }, { inline_data: { mime_type: mime, data } }],
      signal: opts?.signal,
      // temperature 0 = deterministisch (gleiches Bild/Prompt → gleiches Urteil, D34-Geist).
      generationConfig: { temperature: 0, maxOutputTokens: 200 },
    });

    // Safety-/Policy-Sperre ⇒ Input ist ungeeignet ⇒ fail-closed `reject` (sauberes Gate +
    // D52-Refund, KEIN Job-Error; `failOpen` greift hier NICHT — Safety ≠ Format-Hänger).
    // Partiellem Text NICHT vertrauen.
    if (blockReason)
      return {
        verdict: "reject",
        // D115 (Codex-P2): Debug-Parität zum fal-Adapter — dieselben Farb-Felder, hier ohne
        // VLM-Urteil null (bzw. der D114-Pin, falls gesetzt; funktional egal, Job wird abgelehnt).
        score: {
          provider: this.id,
          model: this.model,
          verdictRaw: null,
          reason: `blocked:${blockReason}`,
          rawPreview: "",
          keyColor: req.screenColor ?? null,
          keyColorRaw: null,
          subjectGreen: null,
          subjectBlue: null,
        },
      };

    // Sonst Verdict über den GETEILTEN Helfer (identische D52-Gate-Semantik für alle vision-
    // Provider: fail-closed reject, edit→pass bis image-edit-Provider, rawPreview gekappt).
    const { verdict, reason, verdictRaw, rawPreview, keyColor, keyColorRaw, subjectGreen, subjectBlue } =
      resolveVisionVerdict(text, {
        failOpen: this.failOpen,
        allowEdit: this.allowEdit,
        // D115/D114: gepinnte Re-Validate-Farbe CODE-erzwingen (Prompt-Hinweis allein reicht nicht).
        ...(req.screenColor ? { pinnedScreenColor: req.screenColor } : {}),
      });
    return {
      verdict,
      // D115: keyColorRaw/subjectGreen/subjectBlue im score → Live-Debugging der Farb-Ableitung.
      score: { provider: this.id, model: this.model, verdictRaw, reason, rawPreview, keyColor, keyColorRaw, subjectGreen, subjectBlue },
      ...(keyColor ? { keyColor } : {}), // D79
    };
  }
}

/** §7 llm-Slot → Gemini Text→Text (D60). Veredelt Prompts für prompt-enhance. */
export class GeminiLLMProvider implements LLMProvider {
  readonly id = "gemini-llm";
  readonly version = 1;
  readonly displayName = "Google Gemini (LLM)";

  private readonly baseUrl: string;
  private readonly model: string;

  constructor(private readonly config: GeminiConfig = {}) {
    this.baseUrl = orDefault(config.baseUrl, GEMINI_DEFAULT_BASE_URL).replace(/\/+$/, "");
    this.model = orDefault(config.model, GEMINI_DEFAULT_MODEL);
  }

  private requireKey(): string {
    if (!this.config.apiKey)
      throw new ProviderUnavailableError("llm", this.id, "LLM_API_KEY fehlt (Adapter nicht konfiguriert, §13).");
    return this.config.apiKey;
  }

  async complete(req: LLMRequest): Promise<LLMResult> {
    const apiKey = this.requireKey();
    // D80: optionale Referenzbilder multimodal anhängen (image-generator-Edit-Modus): der LLM
    // SIEHT das hochgeladene Bild und bewahrt dessen Stil. Geladen wie im Vision-Pfad — SSRF-/DoS-
    // gehärtet (https-only, kein Redirect, Größen-/MIME-Limit, private Hosts geblockt). Unbekanntes
    // MIME ⇒ fail-closed (kein HTML-/JSON-Body als „Bild" an Gemini).
    const parts: GeminiPart[] = [{ text: req.prompt }];
    for (const url of req.imageUrls ?? []) {
      const { bytes } = await safeFetchBytes(url, { maxBytes: 32 * 1024 * 1024 });
      // STRIKT fail-closed (Codex P2): das MIME wird AUSSCHLIESSLICH aus den Magic-Bytes bestimmt,
      // NICHT aus dem (fälschbaren) content-type-Header — ein `image/svg+xml` oder fehl-gelabelter
      // HTML-/JSON-Body wird so LOKAL abgelehnt statt als „Bild" an Gemini gereicht. Bewusst
      // strenger als der Vision-Pfad (der dem image/*-Header vertraut): nur die von Gemini sicher
      // unterstützten Raster-Formate (PNG/JPEG/GIF/WebP). Hochgeladene Refs sind beim Upload bereits
      // magic-byte-validiert (D61) ⇒ keine False-Rejects in der Praxis.
      const mime = sniffImageMime(bytes);
      if (!mime)
        throw new Error("Gemini llm: angehängtes Referenzbild ist kein erkennbares Raster-Bild (PNG/JPEG/GIF/WebP) — fail-closed.");
      parts.push({ inline_data: { mime_type: mime, data: toBase64(bytes) } });
    }
    const { text, blockReason } = await generateContent({
      apiKey,
      baseUrl: this.baseUrl,
      model: this.model,
      ...(req.system ? { system: req.system } : {}),
      parts,
      generationConfig: { temperature: 0.4, maxOutputTokens: req.maxTokens ?? 256 },
    });
    // Safety-/Policy-Sperre ⇒ WERFEN (fail-closed, D34) — NICHT still auf den Roh-Prompt
    // degradieren (prompt-enhance fängt nur Provider-Unavailability/-Resolution ab, P1.3).
    if (blockReason) throw new Error(`Gemini: completion blockiert (${blockReason}).`);
    return { text: text.trim() };
  }
}
